import debounce from "../../utils/debounce";
import { cartState } from "./cart-state";

/**
 * Cart drawer.
 *
 * Light DOM on purpose: the drawer's appearance comes from the long-standing
 * `#CartContainer` / `.ajaxcart__*` rules in `assets/theme.css`, and Shadow DOM
 * would cut those off. All behaviour is delegated from the element root, so
 * swapping `innerHTML` after a server render never loses a listener.
 */

const RECOMMENDATIONS_CACHE_PREFIX = "bb:cart-recs:";

function routeRoot(): string {
  const root = window.Shopify?.routes?.root ?? "/";
  return root.endsWith("/") ? root : `${root}/`;
}

/**
 * Whether the drawer is showing.
 *
 * Kept on `<body>` rather than on the element, because that is where the
 * long-standing `[data-cart-active]` rules in `assets/theme.css` look, and
 * because the drawer can be asked to open before it has upgraded. Exported as
 * functions rather than element methods for the same reason — `cart-add-forms`
 * opens the drawer after an add, and should not have to find the element first.
 */
const BODY_ACTIVE_ATTR = "data-cart-active";

export function openDrawer(): void {
  document.body.setAttribute(BODY_ACTIVE_ATTR, "active");
  document.dispatchEvent(new CustomEvent("cart:open"));
}

export function closeDrawer(): void {
  document.body.removeAttribute(BODY_ACTIVE_ATTR);
  document.dispatchEvent(new CustomEvent("cart:close"));
  // No cart write here on purpose. Closing the drawer is not a decision about
  // the cart, and the edits are safe in storage either way.
}

export function isDrawerOpen(): boolean {
  return document.body.hasAttribute(BODY_ACTIVE_ATTR);
}

class CartDrawer extends HTMLElement {
  private unsubscribe: (() => void) | null = null;
  private recommendationsToken = 0;

  connectedCallback(): void {
    cartState.init(Number(this.dataset.cartCount ?? 0));

    this.addEventListener("click", this.onClick);
    this.addEventListener("change", this.onChange);
    this.addEventListener("input", this.onInput);
    this.addEventListener("submit", this.onSubmit);

    document.addEventListener("cart:section", this.onSection);
    document.addEventListener("cart:error", this.onError);
    document.addEventListener("keydown", this.onKeyDown);
    document.addEventListener("click", this.onDocumentClick);

    this.unsubscribe = cartState.subscribe((state) => {
      this.classList.toggle("is-loading", state.busy);
    });

    this.loadRecommendations();
  }

  disconnectedCallback(): void {
    document.removeEventListener("cart:section", this.onSection);
    document.removeEventListener("cart:error", this.onError);
    document.removeEventListener("keydown", this.onKeyDown);
    document.removeEventListener("click", this.onDocumentClick);
    this.unsubscribe?.();
  }

  // ---------------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------------

  private onSection = (event: Event): void => {
    const { id, html } = (event as CustomEvent<{ id: string; html: string }>)
      .detail;
    if (id !== "cart-drawer") return;

    const parsed = new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector("cart-drawer");
    if (!parsed) return;

    const restoreFocus = this.captureFocus();

    this.innerHTML = parsed.innerHTML;
    this.dataset.cartCount = parsed.dataset.cartCount ?? "0";
    this.dataset.cartTotal = parsed.dataset.cartTotal ?? "0";
    this.dataset.multiSize = parsed.dataset.multiSize ?? "false";

    // Fresh markup is internally consistent — if the cart is empty this is now
    // the server's own empty branch — so the client-painted one must come off.
    this.classList.remove("is-empty");

    restoreFocus?.();
    this.loadRecommendations();
  };

  /**
   * A render can land while the drawer is open — an add, or a third-party
   * write — so anything the customer is typing in has to survive the swap.
   * Returns a restorer, or null when focus was not inside the drawer.
   */
  private captureFocus(): (() => void) | null {
    const active = document.activeElement;
    if (!(active instanceof HTMLElement) || !this.contains(active)) return null;

    if (
      active instanceof HTMLTextAreaElement &&
      active.hasAttribute("data-cart-note")
    ) {
      const { value, selectionStart } = active;
      return () => {
        const note =
          this.querySelector<HTMLTextAreaElement>("[data-cart-note]");
        if (!note) return;
        note.value = value;
        note.focus();
        if (selectionStart !== null) {
          note.setSelectionRange(selectionStart, selectionStart);
        }
      };
    }

    const key = active.closest<HTMLElement>("[data-key]")?.dataset.key;
    if (
      key &&
      active instanceof HTMLInputElement &&
      active.matches(".js-qty__num")
    ) {
      const { selectionStart } = active;
      return () => {
        const input = this.querySelector<HTMLInputElement>(
          `[data-key="${CSS.escape(key)}"] .js-qty__num`,
        );
        if (!input) return;
        input.focus();
        if (selectionStart !== null) {
          input.setSelectionRange(selectionStart, selectionStart);
        }
      };
    }

    return null;
  }

  private onError = (event: Event): void => {
    const { message, key } = (
      event as CustomEvent<{ message: string; key?: string }>
    ).detail;

    const target = key
      ? this.querySelector(
          `.ajaxcart__product[data-key="${CSS.escape(key)}"] .ajaxcart__product-error`,
        )
      : null;

    if (target instanceof HTMLElement) {
      target.textContent = message;
      target.style.display = "";
    }
  };

  // ---------------------------------------------------------------------------
  // Open / close
  // ---------------------------------------------------------------------------

  private onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === "Escape" && isDrawerOpen()) {
      closeDrawer();
    }
  };

  /**
   * Opening is triggered from the header bag icon, closing from a click outside
   * the panel. Both live outside this element, hence the document listener.
   */
  private onDocumentClick = (event: MouseEvent): void => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('.cart[href$="/cart"], [data-cart-toggle]');
    if (trigger) {
      event.preventDefault();
      if (isDrawerOpen()) {
        closeDrawer();
      } else {
        openDrawer();
      }
      return;
    }

    if (isDrawerOpen() && !target.closest("cart-drawer")) {
      closeDrawer();
    }
  };

  // ---------------------------------------------------------------------------
  // Delegated interactions
  // ---------------------------------------------------------------------------

  private onClick = (event: MouseEvent): void => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.closest('[data-href="/close"]')) {
      event.preventDefault();
      closeDrawer();
      return;
    }

    const minus = target.closest(".js-qty__adjust--minus");
    if (minus) {
      this.adjustQuantity(minus, -1);
      return;
    }

    const plus = target.closest(".js-qty__adjust--plus");
    if (plus) {
      this.adjustQuantity(plus, 1);
      return;
    }

    const remove = target.closest(".js-qty__adjust--remove");
    if (remove) {
      this.setQuantity(remove, 0);
      return;
    }

    const upsell = target.closest<HTMLElement>("[data-upsell-add]");
    if (upsell) {
      event.preventDefault();
      this.addVariant(Number(upsell.dataset.variantId), upsell);
      return;
    }

    const recommend = target.closest<HTMLElement>("[data-recommendation-add]");
    if (recommend) {
      event.preventDefault();
      this.addRecommendation(recommend);
    }
  };

  private onChange = (event: Event): void => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target instanceof HTMLInputElement && target.matches(".js-qty__num")) {
      const quantity = Math.max(0, Math.floor(Number(target.value) || 0));
      this.setQuantity(target, quantity);
      return;
    }

    if (
      target instanceof HTMLSelectElement &&
      target.matches("[data-recommendation-variant]")
    ) {
      const card = target.closest(".ajaxcart__recommendation");
      const button = card?.querySelector<HTMLElement>(
        "[data-recommendation-add]",
      );
      if (button) button.dataset.selectedVariant = target.value;
    }
  };

  private onInput = (event: Event): void => {
    const target = event.target;
    if (
      target instanceof HTMLTextAreaElement &&
      target.hasAttribute("data-cart-note")
    ) {
      this.saveNote(target.value);
    }
  };

  private saveNote = debounce((note: string) => {
    void cartState.updateNote(note);
  }, 600);

  private onSubmit = (event: SubmitEvent): void => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.id !== "cartform") return;

    const terms = this.querySelector<HTMLInputElement>("[data-cart-terms]");
    if (terms && !terms.checked) {
      event.preventDefault();
      window.alert(window.theme?.strings?.cartConfirmation ?? "");
      return;
    }

    // Verify the cart one last time so checkout never starts from a stale view.
    event.preventDefault();
    const submitter = event.submitter;
    const submitterName = submitter?.getAttribute("name");
    const submitterValue =
      submitter instanceof HTMLButtonElement ||
      submitter instanceof HTMLInputElement
        ? submitter.value
        : "";

    cartState.verifyBeforeCheckout().then((unchanged) => {
      if (!unchanged) return;

      // A pending write can re-render the drawer while we waited (see
      // `onSection`), which replaces the form node — resubmit whatever is live
      // now, not the stale `form` captured above.
      const liveForm = this.querySelector<HTMLFormElement>("#cartform");
      if (!liveForm) return;

      // `form.submit()` drops the clicked button's name/value pair, so
      // Shopify can't tell this apart from a plain quantity update and sends
      // the customer back to the cart page instead of checkout. A hidden
      // field stands in for it — simpler and more reliable than
      // `requestSubmit(submitter)`, which throws if the submitter isn't
      // recognised as owned by this exact form node.
      if (submitterName) {
        let field = liveForm.querySelector<HTMLInputElement>(
          `input[type="hidden"][name="${CSS.escape(submitterName)}"]`,
        );
        if (!field) {
          field = document.createElement("input");
          field.type = "hidden";
          field.name = submitterName;
          liveForm.appendChild(field);
        }
        field.value = submitterValue;
      }

      liveForm.submit();
    });
  };

  // ---------------------------------------------------------------------------
  // Quantity
  // ---------------------------------------------------------------------------

  private lineKey(el: Element): string | null {
    return (
      el.closest<HTMLElement>(".ajaxcart__product")?.dataset.key ??
      el
        .closest(".js-qty__wrapper")
        ?.querySelector<HTMLInputElement>(".js-qty__num")?.dataset.id ??
      null
    );
  }

  private adjustQuantity(el: Element, delta: number): void {
    const input = el
      .closest(".js-qty__wrapper")
      ?.querySelector<HTMLInputElement>(".js-qty__num");
    if (!input) return;

    this.setQuantity(el, Math.max(0, (Number(input.value) || 0) + delta));
  }

  /**
   * Writes the change straight to Shopify (see `cart-state.ts`) and waits for
   * the answer. The row itself does not change here — the `cart:section`
   * re-render this triggers is what updates the quantity and the totals.
   */
  private setQuantity(el: Element, quantity: number): void {
    const key = this.lineKey(el);
    if (!key) return;

    void cartState.setLineQuantity(key, quantity);
  }

  // ---------------------------------------------------------------------------
  // Adding from inside the drawer
  // ---------------------------------------------------------------------------

  private async addVariant(
    variantId: number,
    button: HTMLElement,
  ): Promise<void> {
    if (!variantId) return;

    button.setAttribute("disabled", "disabled");
    cartState.applyOptimisticDelta(1);

    const result = await cartState.add({ id: variantId, quantity: 1 });
    if (!result) cartState.applyOptimisticDelta(-1);

    button.removeAttribute("disabled");
  }

  private async addRecommendation(button: HTMLElement): Promise<void> {
    const card = button.closest(".ajaxcart__recommendation");
    const errorEl = card?.querySelector<HTMLElement>(
      "[data-recommendation-error]",
    );
    const container = this.querySelector<HTMLElement>(
      "[data-cart-recommendations]",
    );

    const variantId = Number(button.dataset.selectedVariant || 0);

    if (!variantId) {
      if (errorEl) {
        errorEl.textContent = container?.dataset.errorSelect ?? "";
      }
      return;
    }

    if (errorEl) errorEl.textContent = "";
    await this.addVariant(variantId, button);
  }

  // ---------------------------------------------------------------------------
  // Recommendations
  // ---------------------------------------------------------------------------

  private async loadRecommendations(): Promise<void> {
    const container = this.querySelector<HTMLElement>(
      "[data-cart-recommendations]",
    );
    if (!container) return;

    const list = container.querySelector<HTMLElement>(
      "[data-recommendations-list]",
    );
    const productId = container.dataset.productId;

    if (!list || !productId) {
      container.classList.add("is-hidden");
      return;
    }

    const cacheKey = `${RECOMMENDATIONS_CACHE_PREFIX}${productId}`;
    const cached = this.readCachedRecommendations(cacheKey);
    if (cached !== null) {
      this.paintRecommendations(container, list, cached);
      return;
    }

    const token = ++this.recommendationsToken;

    try {
      const url =
        `${routeRoot()}recommendations/products` +
        `?section_id=cart-recommendations&product_id=${encodeURIComponent(productId)}` +
        `&limit=2&intent=related`;

      const response = await window.fetch(url, { credentials: "same-origin" });
      if (!response.ok) throw new Error("Recommendations request failed");

      const html = await response.text();
      if (token !== this.recommendationsToken) return;

      this.writeCachedRecommendations(cacheKey, html);
      this.paintRecommendations(container, list, html);
    } catch {
      if (token !== this.recommendationsToken) return;
      container.classList.add("is-hidden");
      list.classList.remove("is-loading");
      list.innerHTML = "";
    }
  }

  private paintRecommendations(
    container: HTMLElement,
    list: HTMLElement,
    html: string,
  ): void {
    const grid = new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(".ajaxcart__recommendations-grid");

    list.classList.remove("is-loading");

    if (!grid) {
      container.classList.add("is-hidden");
      list.innerHTML = "";
      return;
    }

    container.classList.remove("is-hidden");
    list.innerHTML = grid.outerHTML;
  }

  private readCachedRecommendations(key: string): string | null {
    try {
      return window.sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private writeCachedRecommendations(key: string, html: string): void {
    try {
      window.sessionStorage.setItem(key, html);
    } catch {
      // Quota or private mode — caching is an optimisation only.
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "cart-drawer": CartDrawer;
  }
}

if (!window.customElements.get("cart-drawer")) {
  window.customElements.define("cart-drawer", CartDrawer);
}

export {};
